import React from 'react'
import { Phone, MapPin, Clock, Check } from 'lucide-react'
export type LocationData = {
  name: string
  address: string
  city: string
  state: string
  zip: string
  phone: string
  hours: {
    weekday: string
    saturday: string
    sunday: string
  }
  services: string[]
  mapUrl: string
  slug: string;
}
  
type ConsultationLocationSectionProps = {
  location: LocationData;
}
export const ConsultationLocationSection = ({
  location,
}: ConsultationLocationSectionProps) => {
  return (
    <section className="!bg-slate-200 w-full py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Section Header */}
          <h2 className="text-3xl text-center mb-6 text-base sm:text-3xl">
            ON-SITE CONSULTATION IN {location.city.toUpperCase()}
          </h2>
          {/* Action Buttons - Below header */}
          <div className="grid grid-cols-2 gap-4 mb-8 max-w-md mx-auto">
            <a
              href={`tel:${location.phone.replace(/[^0-9]/g, '')}`}
              className="bg-primary text-white font-bold py-3 px-4 rounded-md text-center flex items-center justify-center transition-all hover:bg-primary/90 text-xs whitespace-nowrap"
            >
              <Phone className="w-5 h-5 mr-2" />
              CALL US
            </a>
            <a
              href={`https://www.google.com/maps/search/?api=${process.env.NEXT_PUBLIC_MAPS_EMBED_API_KEY}&query=${encodeURIComponent(`${location.address}, ${location.city}, ${location.state} ${location.zip}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 text-white font-bold py-3 px-4 rounded-md text-center flex items-center justify-center transition-all hover:bg-green-500/90 text-xs whitespace-nowrap"
            >
              <MapPin className="w-5 h-5 mr-2" />
              GET DIRECTIONS
            </a>
          </div>
          {/* Main Content - Map and Location Card */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Map Section - Moved up for mobile view */}
            <div className="w-full lg:w-1/2 order-1 lg:order-2">
              <div className="bg-white rounded-lg shadow-lg h-full overflow-hidden">
                <iframe
                  src={`https://www.google.com/maps?q=${encodeURIComponent(`${location.address}, ${location.city}, ${location.state} ${location.zip}`)}&output=embed`}
                  width="100%"
                  height="100%"
                  className="border-0 min-h-[300px] lg:min-h-[450px]"
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title={`Map of ${location.city} Location`}
                ></iframe>
              </div>
            </div>
            {/* Location Card */}
            <div className="w-full lg:w-1/2 order-2 lg:order-1 mt-6 lg:mt-0">
              <div className="rounded-lg overflow-hidden shadow-lg h-full">
                {/* Header section */}
                <div className="bg-[#03182A] text-white p-4">
                  <div className="flex items-start">
                    <MapPin className="text-primary mt-1 mr-2 flex-shrink-0" />
                    <div>
                      <h2 className="text-lg">{location.city} Location</h2>
                      <p className="text-sm">
                        {location.address}, {location.city}, {location.state}{' '}
                        {location.zip}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Content section */}
                <div className="bg-white p-4">
                  {/* Contact */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center mr-2">
                        <Phone className="w-4 h-4 text-navy" />
                      </div>
                      <h3 className="text-gray-800">Contact</h3>
                    </div>
                    <a
                      href={`tel:${location.phone.replace(/[^0-9]/g, '')}`}
                      className="text-primary font-medium block ml-10"
                    >
                      {location.phone}
                    </a>
                  </div>
                  {/* Hours */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center mr-2">
                        <Clock className="w-4 h-4 text-navy" />
                      </div>
                      <h3 className="text-gray-800">Hours</h3>
                    </div>
                    <div className="ml-10 space-y-2">
                      <div className="flex justify-between">
                        <p className="text-xs font-bold uppercase text-gray-500">
                          WEEKDAYS
                        </p>
                        <p className="text-sm">{location.hours.weekday}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-xs font-bold uppercase text-gray-500">
                          SATURDAY
                        </p>
                        <p className="text-sm">{location.hours.saturday}</p>
                      </div>
                      <div className="flex justify-between">
                        <p className="text-xs font-bold uppercase text-gray-500">
                          SUNDAY
                        </p>
                        <p className="text-sm">{location.hours.sunday}</p>
                      </div>
                    </div>
                  </div>
                  {/* Services */}
                  <div className="mb-4">
                    <div className="flex items-center mb-2">
                      <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center mr-2">
                        <MapPin className="w-4 h-4 text-navy" />
                      </div>
                      <h3 className="text-gray-800">Services at this location</h3>
                    </div>
                    <ul className="ml-10 space-y-2">
                      {location.services.map((service, index) => (
                        <li key={index} className="flex items-start">
                          <div className="mt-0.5 mr-2 text-navy flex-shrink-0">
                            <Check className="w-4 h-4" />
                          </div>
                          <span className="text-sm">{service}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {/* Footer */}
                <div className="border-t border-gray-200 p-3 bg-white">
                  <p className="text-xs text-center text-gray-700">
                  All brake repairs are completed by NuBrakes mobile technicians at your home or office, or by appointment at {location.address}, {location.city}, {location.state} {location.zip}.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
